import { useState, useRef, useEffect } from "react";
import {
  UploadIcon,
  PencilIcon,
  TrashIcon,
  PhotographIcon,
  CheckIcon,
} from "@heroicons/react/outline";
import {
  uploadTableMap,
  getTableMap,
  updateTableMap,
  updateTable,
  updateEvent,
} from "../../../../api/eventsApi";
import { CURRENCY_MAP } from "../../../../constants";
import { getAllTicketTypesByEvent } from "../../../../api/ticketTypesApi";
import Toast from "../../../../components/toast";
import ToastContainer from "../../../../components/toastContainer";

const isComplete = (t) =>
  t.price && t.drinks_credit && t.tickets_per_table && t.commission;

const TablesTab = ({ event, onRefreshEvent, isViewOnly }) => {
  const [mapPreview, setMapPreview] = useState(event?.table_map || null);
  const [tables, setTables] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());
  const [deleteTableId, setDeleteTableId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [mapEditMode, setMapEditMode] = useState(false);
  const [mapSaving, setMapSaving] = useState(false);
  const [changedIds, setChangedIds] = useState(new Set());
  const [imgNaturalSize, setImgNaturalSize] = useState({ w: 0, h: 0 });

  const [toast, setToast] = useState(null);

  const [ticketTypes, setTicketTypes] = useState([]);

  const [editingTable, setEditingTable] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const [savingTable, setSavingTable] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [showCopyDropdown, setShowCopyDropdown] = useState(false);

  const [isTablesLive, setIsTablesLive] = useState(
    event?.is_tables_live || false,
  );
  const [togglingLive, setTogglingLive] = useState(false);

  const fileInputRef = useRef(null);
  const mapRef = useRef(null);
  const resizeStartRef = useRef(null);

  const preEditTablesRef = useRef(null);

  const preEditTableRef = useRef(null);

  const copyDropdownRef = useRef(null);

  const selectedTable = tables?.find((t) => t.id === selectedId);
  const completedCount = tables.filter((t) => isComplete(t)).length;

  const getCurrencySymbol = (currency) => {
    const symbol = CURRENCY_MAP[currency] || currency;
    return `${symbol}`;
  };

  useEffect(() => {
    if (event?.table_map) {
      setMapPreview(event.table_map);
      fetchTables();
    }
    fetchTicketTypes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        copyDropdownRef.current &&
        !copyDropdownRef.current.contains(e.target)
      ) {
        setShowCopyDropdown(false);
      }
    };
    if (showCopyDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCopyDropdown]);

  const fetchTables = async () => {
    try {
      const data = await getTableMap(event.id);
      const tableList = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.tables)
            ? data.tables
            : [];
      setTables(tableList);
    } catch (err) {
      console.error("Failed to fetch tables", err);
    }
  };

  const fetchTicketTypes = async () => {
    try {
      const allTicketTypeData = await getAllTicketTypesByEvent(
        999,
        0,
        event.id,
      );
      setTicketTypes(
        allTicketTypeData.results.filter((val) => val.type === "table") || [],
      );
    } catch (err) {
      //error
    }
  };

  const handleToggleTablesLive = async () => {
    if (isViewOnly) return;
    try {
      setTogglingLive(true);
      const newValue = !isTablesLive;
      //await onRefreshEvent(); // or call updateEvent directly if you have it imported
      // TODO: replace with your actual event update API callimport ToastContainer from '../../../../components/toastContainer';

      await updateEvent(event.id, { is_tables_live: newValue });
      setIsTablesLive(newValue);
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail || "Failed to load artists",
      });
    } finally {
      setTogglingLive(false);
    }
  };

  const handleMapUpload = async (e) => {
    if (isViewOnly) return;
    const file = e.target.files[0];
    if (!file) return;

    setUploadError(null);

    const isValidSize = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve(img.width === 1080 && img.height === 1350);
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => resolve(false);
      img.src = URL.createObjectURL(file);
    });

    if (!isValidSize) {
      setUploadError(
        "Image must be exactly 1080 × 1350 pixels. Please resize and try again.",
      );
      fileInputRef.current.value = "";
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("table_map", file);
      uploadTableMap(event.id, formData);
    } catch (err) {
      setUploadError(
        err?.response?.data?.detail ||
          err?.detail ||
          "Upload failed. Please try again.",
      );
      setUploading(false);
      return;
    }

    setMapPreview(URL.createObjectURL(file));

    await new Promise((resolve) => setTimeout(resolve, 2000));

    try {
      const data = await getTableMap(event.id);
      const tableList = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.tables)
            ? data.tables
            : [];
      setTables(tableList);
    } catch (err) {
      console.error("Failed to fetch tables after upload:", err);
    }

    try {
      await onRefreshEvent();
    } catch (err) {
      console.error("Refresh failed:", err);
    }

    setUploading(false);
  };

  const handleImageLoad = (e) => {
    setImgNaturalSize({
      w: e.target.naturalWidth,
      h: e.target.naturalHeight,
    });
  };

  const handleFormChange = (key, value) => {
    setTables((prev) =>
      prev.map((t) => (t.id === selectedId ? { ...t, [key]: value } : t)),
    );
  };

  const handleCopyFrom = (sourceId) => {
    const source = tables.find((t) => t.id === sourceId);
    if (!source) return;
    setShowCopyDropdown(false);

    setTables((prev) =>
      prev.map((t) => {
        if (t.id === selectedId) {
          return {
            ...t,
            price: source.price,
            commission: source.commission,
            commission_percentage: source.commission_percentage,
            capacity: source.capacity,
            drinks_credit_enabled: source.drinks_credit_enabled,
            drinks_credit_amount: source.drinks_credit_amount,
            reservation_status: source.reservation_status,
            tickets_included_enabled: source.tickets_included_enabled,
            tickets_included_ticket_type:
              source.tickets_included_ticket_type || null,
            merchandise_enabled: source.merchandise_enabled,
            merchandise_description: source.merchandise_description,
            description: source.description,
          };
        }
        return t;
      }),
    );
  };

  const handleSave = async () => {
    if (!selectedTable) return;
    setSaveError(null);

    try {
      setSavingTable(true);

      const payload = {
        price: selectedTable.price,
        commission: selectedTable.commission,
        commission_percentage: selectedTable.commission_percentage,
        capacity: selectedTable.capacity,
        drinks_credit_enabled: selectedTable.drinks_credit_enabled,
        drinks_credit_amount: selectedTable.drinks_credit_amount,
        tickets_included_enabled: selectedTable.tickets_included_enabled,
        tickets_included_ticket_type:
          selectedTable.tickets_included_ticket_type || null,
        merchandise_enabled: selectedTable.merchandise_enabled,
        merchandise_description: selectedTable.merchandise_description,
        reservation_status: selectedTable.reservation_status,
        reserved_customer_name: selectedTable.reservation_name,
        reserved_customer_phone: selectedTable.reservation_phone,
        reserved_customer_email: selectedTable.reservation_email,
        reserved_customer_address: selectedTable.reservation_address,
        description: selectedTable.description,
      };
      const updated = await updateTable(selectedTable.id, event.id, payload);

      setTables((prev) =>
        prev.map((t) => (t.id === selectedTable.id ? { ...t, ...updated } : t)),
      );

      setSavedIds((prev) => new Set([...prev, selectedId]));

      const currentIndex = tables.findIndex((t) => t.id === selectedId);
      const next = tables.find(
        (t, i) =>
          i > currentIndex && !savedIds.has(t.id) && t.id !== selectedId,
      );
      if (next) setSelectedId(next.id);
    } catch (err) {
      setSaveError(
        err?.response?.data?.detail || err?.detail || "Failed to save table.",
      );
    } finally {
      setSavingTable(false);
    }
  };

  const handleEditOpen = (t) => {
    setEditingTable(t);
    setEditForm({
      price: t.price || "",
      drinks_credit_amount: t.drinks_credit_amount || "",
      capacity: t.capacity || "",
    });
  };

  const handleEditSave = async () => {
    try {
      setEditSaving(true);
      const updated = await updateTable(editingTable.id, event.id, editForm);
      setTables((prev) =>
        prev.map((t) => (t.id === editingTable.id ? { ...t, ...updated } : t)),
      );
      setEditingTable(null);
    } catch (err) {
      console.error("Failed to update table:", err);
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = (id) => {
    setTables((prev) => prev.filter((t) => t.id !== id));
    setSavedIds((prev) => {
      prev.delete(id);
      return new Set(prev);
    });
    if (selectedId === id) setSelectedId(null);
    setDeleteTableId(null);
  };

  // ── Drag to reposition hotspot ──
  const handleHotspotDragStart = (e, id) => {
    if (isViewOnly) return; // ← block drag for viewers
    e.stopPropagation();
    e.preventDefault();

    const mapEl = mapRef.current;
    if (!mapEl) return;

    const onMouseMove = (moveEvent) => {
      const rect = mapEl.getBoundingClientRect();
      const xPct = Math.max(
        0,
        Math.min(100, ((moveEvent.clientX - rect.left) / rect.width) * 100),
      );
      const yPct = Math.max(
        0,
        Math.min(100, ((moveEvent.clientY - rect.top) / rect.height) * 100),
      );

      const newMapX = Math.round((xPct / 100) * imgNaturalSize.w);
      const newMapY = Math.round((yPct / 100) * imgNaturalSize.h);

      setChangedIds((prev) => new Set([...prev, id]));

      setTables((prev) =>
        prev.map((t) =>
          t.id === id ? { ...t, map_x: newMapX, map_y: newMapY } : t,
        ),
      );
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  // ── Resize hotspot ──
  const handleResizeStart = (e, id, currentSize) => {
    if (isViewOnly) return; // ← block resize for viewers
    e.stopPropagation();
    e.preventDefault();
    resizeStartRef.current = { startY: e.clientY, startSize: currentSize };

    const onMouseMove = (moveEvent) => {
      const delta = moveEvent.clientY - resizeStartRef.current.startY;
      const newSize = Math.max(
        16,
        Math.min(80, resizeStartRef.current.startSize + delta),
      );

      setChangedIds((prev) => new Set([...prev, id]));

      setTables((prev) =>
        prev.map((t) =>
          t.id === id ? { ...t, map_size: Math.round(newSize) } : t,
        ),
      );
    };

    const onMouseUp = () => {
      resizeStartRef.current = null;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const handleCancelEditing = () => {
    if (preEditTablesRef.current) {
      setTables(preEditTablesRef.current); // revert to snapshot
    }
    setChangedIds(new Set());
    setMapEditMode(false);
    preEditTablesRef.current = null;
  };

  const selectTable = (id) => {
    const table = tables.find((t) => t.id === id);
    preEditTableRef.current = table ? { ...table } : null;
    setSelectedId(id);
    setSaveError(null);
  };

  const handleCancelForm = () => {
    if (preEditTableRef.current) {
      setTables((prev) =>
        prev.map((t) =>
          t.id === preEditTableRef.current.id ? preEditTableRef.current : t,
        ),
      );
    }
    setSaveError(null);
    setSelectedId(null);
    preEditTableRef.current = null;
  };

  // ── Done editing — bulk save changed tables ──
  const handleDoneEditing = async () => {
    if (changedIds.size === 0) {
      setMapEditMode(false);
      return;
    }

    try {
      setMapSaving(true);

      const payload = {
        tables: tables
          .filter((t) => changedIds.has(t.id))
          .map((t) => ({
            id: t.id,
            map_x: t.map_x,
            map_y: t.map_y,
            map_size: t.map_size,
            map_shape: t.map_shape,
          })),
      };
      await updateTableMap(event.id, payload);

      setChangedIds(new Set());
      setMapEditMode(false);
    } catch (err) {
      setUploadError(
        err?.response?.data?.detail ||
          err?.detail ||
          "Failed to save map changes. Please try again.",
      );
    } finally {
      setMapSaving(false);
    }
  };

  const statusBadgeStyle = (status) => {
    switch (status) {
      case "available":
        return "bg-emerald-500/10 border-emerald-500/20 text-emerald-400";
      case "pending":
        return "bg-amber-500/10 border-amber-500/20 text-amber-400";
      case "reserved":
        return "bg-red-500/10 border-red-500/20 text-red-400";
      default:
        return "bg-slate-500/10 border-slate-500/20 text-slate-400";
    }
  };

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-white">Tables</h3>
          {tables.length > 0 && (
            <p className="text-xs text-slate-500 mt-0.5">
              {completedCount} of {tables.length} tables configured
            </p>
          )}
        </div>

        {/* toggle disabled + visually locked for viewers */}
        <div className="flex items-center gap-2.5">
          <span className="text-xs text-slate-400">Display on shop</span>
          <button
            onClick={handleToggleTablesLive}
            disabled={togglingLive || isViewOnly}
            className={`relative inline-flex w-10 h-5 rounded-full transition-colors disabled:opacity-50 ${
              isViewOnly ? "cursor-not-allowed" : ""
            } ${isTablesLive ? "bg-pink-600" : "bg-slate-700"}`}
          >
            <span
              className={`inline-block w-4 h-4 bg-white rounded-full shadow transform transition-transform mt-0.5 ${isTablesLive ? "translate-x-5" : "translate-x-0.5"}`}
            />
          </button>
          <span
            className={`text-xs font-medium ${isTablesLive ? "text-emerald-400" : "text-slate-500"}`}
          >
            {togglingLive ? "..." : isTablesLive ? "Live" : "Off"}
          </span>
        </div>

        {/* hide upload button for viewers */}
        {!isViewOnly && mapPreview && (
          <button
            onClick={() => !uploading && fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 border border-white/10 hover:bg-slate-800 text-slate-300 hover:text-white px-4 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/10 border-t-pink-500 rounded-full animate-spin" />
                Analysing...
              </>
            ) : (
              <>
                <UploadIcon className="w-4 h-4" />
                Upload Map
              </>
            )}
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          className="hidden"
          onChange={handleMapUpload}
        />
      </div>

      {uploadError && (
        <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
          {uploadError}
        </div>
      )}

      {/* hide upload drop zone for viewers when no map exists */}
      {!mapPreview ? (
        !isViewOnly ? (
          <button
            onClick={() => !uploading && fileInputRef.current?.click()}
            disabled={uploading}
            className={`w-full h-64 rounded-xl border-2 border-dashed transition flex flex-col items-center justify-center gap-3
              ${uploading ? "border-white/5 bg-slate-800/20 cursor-not-allowed" : "border-white/10 hover:border-pink-500/40 hover:bg-pink-500/5 cursor-pointer text-slate-500 hover:text-slate-300"}`}
          >
            {uploading ? (
              <>
                <div className="w-10 h-10 border-2 border-white/10 border-t-pink-500 rounded-full animate-spin" />
                <div className="text-center">
                  <p className="text-sm text-slate-300">
                    Analysing table map...
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    This may take a few seconds
                  </p>
                </div>
              </>
            ) : (
              <>
                <PhotographIcon className="w-12 h-12" />
                <div className="text-center">
                  <p className="text-sm">Upload your table map</p>
                  <p className="text-xs text-slate-600 mt-1">
                    JPEG, JPG, PNG · Tables will be auto-detected
                  </p>
                  <p className="text-xs text-slate-600 mt-1">
                    Recommended Size: 1080 x 1350
                  </p>
                </div>
              </>
            )}
          </button>
        ) : (
          <div className="w-full h-40 rounded-xl border border-white/5 flex items-center justify-center text-slate-600 text-sm">
            No table map uploaded yet
          </div>
        )
      ) : (
        <>
          {/* ── Interactive map + form ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left — map */}
            <div className="bg-slate-900/60 border border-white/10 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                <p className="text-xs text-slate-400">
                  {mapEditMode
                    ? "Drag to move · amber handle to resize"
                    : "Click a table on the map to view details"}
                </p>

                {/* hide Edit Map button for viewers */}
                {!isViewOnly && (
                  <div className="flex items-center gap-2">
                    {mapEditMode && (
                      <button
                        onClick={handleCancelEditing}
                        disabled={mapSaving}
                        className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-slate-400 hover:text-white hover:bg-slate-800 transition disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (mapEditMode) {
                          handleDoneEditing();
                        } else {
                          preEditTablesRef.current = tables;
                          setChangedIds(new Set());
                          setMapEditMode(true);
                        }
                      }}
                      disabled={mapSaving}
                      className={`text-xs px-3 py-1.5 rounded-lg border transition font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                        mapEditMode
                          ? "bg-pink-600 border-pink-500 text-white"
                          : "border-white/10 text-slate-400 hover:text-white hover:bg-slate-800"
                      }`}
                    >
                      {mapSaving ? (
                        <span className="flex items-center gap-1.5">
                          <div className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                          Saving...
                        </span>
                      ) : mapEditMode ? (
                        "Done Editing"
                      ) : (
                        "Edit Map"
                      )}
                    </button>
                  </div>
                )}
              </div>

              <div className="relative" ref={mapRef}>
                <img
                  src={mapPreview}
                  alt="Table map"
                  className="w-full object-contain block"
                  draggable={false}
                  onLoad={handleImageLoad}
                />

                {tables.map((t) => {
                  const isSelected = t.id === selectedId;
                  const complete = isComplete(t);
                  const xPct = imgNaturalSize.w
                    ? (t.map_x / imgNaturalSize.w) * 100
                    : 0;
                  const yPct = imgNaturalSize.h
                    ? (t.map_y / imgNaturalSize.h) * 100
                    : 0;
                  const size = t.map_size || 24;
                  const isCircle = t.map_shape === "circle";
                  const isChanged = changedIds.has(t.id);

                  const getHotspotStyle = () => {
                    switch (t.display_status) {
                      case "available":
                        return "bg-[#03211a] border-green-900/50 text-gray-400 hover:scale-110 cursor-pointer";
                      case "pending":
                        return "bg-yellow-600/80 border-yellow-400 text-white cursor-not-allowed";
                      case "reserved":
                        return "bg-red-600/80 border-red-400 text-white cursor-not-allowed";
                      case "sold_out":
                        return "bg-red-800/80 border-red-600 text-white cursor-not-allowed";
                      default:
                        return "bg-[#03211a] border-green-900/50 text-gray-400 hover:scale-110 cursor-pointer";
                    }
                  };

                  return (
                    <div
                      key={t.id}
                      style={{
                        position: "absolute",
                        left: `${xPct}%`,
                        top: `${yPct}%`,
                        width: `${size}px`,
                        height: `${size}px`,
                        transform: "translate(-50%, -50%)",
                        zIndex: isSelected && !mapEditMode ? 20 : 10,
                      }}
                    >
                      <button
                        onMouseDown={(e) =>
                          !isViewOnly &&
                          mapEditMode &&
                          handleHotspotDragStart(e, t.id)
                        }
                        onClick={() => !mapEditMode && selectTable(t.id)}
                        style={{
                          width: "100%",
                          height: "100%",
                          fontSize: `${Math.max(8, size * 0.35)}px`,
                          borderRadius: isCircle ? "50%" : "4px",
                          cursor:
                            mapEditMode && !isViewOnly ? "grab" : "pointer",
                          userSelect: "none",
                        }}
                        className={`font-bold border-2 shadow-lg flex items-center justify-center w-full h-full
                          ${!mapEditMode ? "transition-all" : "transition-none"}
                          ${
                            isSelected && !mapEditMode
                              ? `bg-pink-600 border-white text-white scale-125 ${getHotspotStyle()}`
                              : mapEditMode && !isViewOnly
                                ? isChanged
                                  ? "bg-amber-600/80 border-amber-300 text-white active:cursor-grabbing"
                                  : "bg-slate-700/90 border-amber-400/60 text-slate-300 active:cursor-grabbing"
                                : getHotspotStyle()
                          }`}
                        title={t.name}
                      >
                        {t.name}
                      </button>

                      {/* hide resize handle for viewers */}
                      {mapEditMode && !isViewOnly && (
                        <div
                          onMouseDown={(e) => handleResizeStart(e, t.id, size)}
                          style={{
                            position: "absolute",
                            bottom: "-4px",
                            right: "-4px",
                            width: "10px",
                            height: "10px",
                            background: "#f59e0b",
                            borderRadius: "2px",
                            cursor: "se-resize",
                            border: "1px solid rgba(255,255,255,0.4)",
                            zIndex: 30,
                          }}
                          title="Drag to resize"
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {mapEditMode && !isViewOnly && (
                <div className="px-4 py-3 border-t border-white/10 flex items-center gap-4">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-slate-700 border border-amber-400/60" />
                    <span className="text-xs text-slate-400">Drag to move</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-amber-400" />
                    <span className="text-xs text-slate-400">
                      Drag to resize
                    </span>
                  </div>

                  {/* UNIVERSAL SIZE CONTROL */}
                  <div className="flex items-center gap-2 ml-auto">
                    <span className="text-xs text-slate-400">Size</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          setTables((prev) =>
                            prev.map((t) => ({
                              ...t,
                              map_size: Math.max(16, (t.map_size || 24) - 4),
                            })),
                          );
                          setChangedIds(new Set(tables.map((t) => t.id)));
                        }}
                        className="w-6 h-6 rounded border border-white/10 text-slate-300 hover:bg-slate-700 hover:text-white transition flex items-center justify-center text-sm"
                      >
                        −
                      </button>
                      <span className="text-xs text-slate-400 w-8 text-center tabular-nums">
                        {tables[0]?.map_size || 24}px
                      </span>
                      <button
                        onClick={() => {
                          setTables((prev) =>
                            prev.map((t) => ({
                              ...t,
                              map_size: Math.min(80, (t.map_size || 24) + 4),
                            })),
                          );
                          setChangedIds(new Set(tables.map((t) => t.id)));
                        }}
                        className="w-6 h-6 rounded border border-white/10 text-slate-300 hover:bg-slate-700 hover:text-white transition flex items-center justify-center text-sm"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {changedIds.size > 0 && (
                    <div className="flex items-center pl-3 gap-1.5">
                      <div className="w-3 h-3 rounded bg-amber-600/80 border border-amber-300" />
                      <span className="text-xs text-amber-400">
                        {changedIds.size} unsaved change
                        {changedIds.size !== 1 ? "s" : ""}
                      </span>
                    </div>
                  )}
                </div>
              )}
              <div className="px-4 py-3 border-t border-green-900/20 flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded border-2 bg-[#03211a] border-green-900/50" />
                  <span className="text-xs text-gray-400">Disponible</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded border-2 bg-yellow-600/80 border-yellow-400" />
                  <span className="text-xs text-gray-400">Pendiente</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded border-2 bg-red-600/80 border-red-400" />
                  <span className="text-xs text-gray-400">
                    Reservado / Agotado
                  </span>
                </div>
              </div>
            </div>

            {/* Right — form panel */}
            <div className="bg-slate-900/60 border border-white/10 rounded-xl flex flex-col">
              {selectedTable && !mapEditMode ? (
                <>
                  <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-pink-600/20 border border-pink-500/30 text-pink-400 text-sm font-bold flex items-center justify-center">
                        {selectedTable.name}
                      </div>

                      {/* hide copy dropdown for viewers */}
                      {!isViewOnly && (
                        <div className="relative" ref={copyDropdownRef}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowCopyDropdown((v) => !v);
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-slate-400 hover:text-white hover:bg-slate-800 text-xs transition"
                          >
                            Copy from ↓
                          </button>
                          {showCopyDropdown && (
                            <div className="absolute left-0 top-full mt-1 w-48 bg-slate-900 border border-white/10 rounded-xl shadow-xl z-50 overflow-hidden">
                              <p className="px-3 py-2 text-[10px] text-slate-500 uppercase tracking-widest border-b border-white/5">
                                Copy info from
                              </p>
                              <div className="max-h-48 overflow-y-auto">
                                {tables
                                  .filter((t) => t.id !== selectedId)
                                  .map((t) => (
                                    <button
                                      key={t.id}
                                      onClick={() => handleCopyFrom(t.id)}
                                      className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition"
                                    >
                                      {t.name}
                                    </button>
                                  ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* hide Save Table button for viewers */}
                    {!isViewOnly && (
                      <button
                        onClick={handleSave}
                        disabled={savingTable}
                        className="px-5 py-2 text-sm rounded-lg text-white bg-pink-600 hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
                      >
                        {savingTable ? (
                          <>
                            <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                            Saving...
                          </>
                        ) : (
                          "Save Table"
                        )}
                      </button>
                    )}
                  </div>

                  {/* read-only view for viewers, full form for editors */}
                  <div className="px-5 py-5 flex-1 overflow-y-auto space-y-4">
                    {isViewOnly ? (
                      <div className="space-y-3">
                        {[
                          {
                            label: "Price",
                            value: selectedTable.price
                              ? `${getCurrencySymbol(event.currency)} ${selectedTable.price}`
                              : "—",
                          },
                          {
                            label: "Capacity",
                            value: selectedTable.capacity || "—",
                          },
                          {
                            label: "Commission Type",
                            value: selectedTable.commission || "—",
                          },
                          {
                            label: "Commission %",
                            value: selectedTable.commission_percentage
                              ? `${selectedTable.commission_percentage}%`
                              : "—",
                          },
                          {
                            label: "Drinks Credit",
                            value: selectedTable.drinks_credit_enabled
                              ? `${getCurrencySymbol(event.currency)} ${selectedTable.drinks_credit_amount || "0"}`
                              : "Disabled",
                          },
                          {
                            label: "Tickets Included",
                            value: selectedTable.tickets_included_enabled
                              ? "Yes"
                              : "No",
                          },
                          {
                            label: "Status",
                            value: selectedTable.reservation_status || "—",
                          },
                        ].map(({ label, value }) => (
                          <div key={label}>
                            <p className="text-xs text-slate-500">{label}</p>
                            <p className="text-sm text-white mt-0.5">{value}</p>
                          </div>
                        ))}
                        {selectedTable.description && (
                          <div>
                            <p className="text-xs text-slate-500">
                              Description
                            </p>
                            <p className="text-sm text-slate-300 mt-0.5">
                              {selectedTable.description}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <Field
                            label={`Price (${getCurrencySymbol(event.currency)})`}
                          >
                            <input
                              type="number"
                              value={selectedTable.price || ""}
                              onChange={(e) =>
                                handleFormChange("price", e.target.value)
                              }
                              placeholder="0.00"
                              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                            />
                          </Field>
                          <Field label="Commission (%)">
                            <input
                              type="number"
                              value={selectedTable.commission_percentage || ""}
                              onChange={(e) =>
                                handleFormChange(
                                  "commission_percentage",
                                  e.target.value,
                                )
                              }
                              placeholder="0"
                              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                            />
                          </Field>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <Field label="Capacity (Guests)">
                            <input
                              type="number"
                              value={selectedTable.capacity || ""}
                              onChange={(e) =>
                                handleFormChange("capacity", e.target.value)
                              }
                              placeholder="0"
                              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                            />
                          </Field>
                        </div>

                        <div className="bg-slate-800/40 border border-white/5 rounded-lg p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="text-sm text-slate-300">
                              Drinks Credit Included
                            </label>
                            <button
                              onClick={() =>
                                handleFormChange(
                                  "drinks_credit_enabled",
                                  !selectedTable.drinks_credit_enabled,
                                )
                              }
                              className={`relative inline-flex w-10 h-5 rounded-full transition-colors ${selectedTable.drinks_credit_enabled ? "bg-pink-600" : "bg-slate-700"}`}
                            >
                              <span
                                className={`inline-block w-4 h-4 bg-white rounded-full shadow transform transition-transform mt-0.5 ${selectedTable.drinks_credit_enabled ? "translate-x-5" : "translate-x-0.5"}`}
                              />
                            </button>
                          </div>
                          {selectedTable.drinks_credit_enabled && (
                            <Field
                              label={`Drink Credit Amount (${getCurrencySymbol(event.currency)})`}
                            >
                              <input
                                type="number"
                                value={selectedTable.drinks_credit_amount || ""}
                                onChange={(e) =>
                                  handleFormChange(
                                    "drinks_credit_amount",
                                    e.target.value,
                                  )
                                }
                                placeholder="0.00"
                                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                              />
                            </Field>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <Field label="Commission Type">
                            <div className="grid grid-cols-2 gap-2">
                              {["client", "seller"].map((c) => (
                                <button
                                  key={c}
                                  onClick={() =>
                                    handleFormChange("commission", c)
                                  }
                                  className={`py-2 rounded-lg text-xs font-medium capitalize border transition ${selectedTable.commission === c ? "bg-pink-500/20 border-pink-500/50 text-pink-400" : "bg-slate-800 border-white/10 text-slate-400 hover:border-white/20 hover:text-white"}`}
                                >
                                  {c}
                                </button>
                              ))}
                            </div>
                          </Field>
                        </div>

                        <div className="bg-slate-800/40 border border-white/5 rounded-lg p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="text-sm text-slate-300">
                              Tickets Included
                            </label>
                            <button
                              onClick={() =>
                                handleFormChange(
                                  "tickets_included_enabled",
                                  !selectedTable.tickets_included_enabled,
                                )
                              }
                              className={`relative inline-flex w-10 h-5 rounded-full transition-colors ${selectedTable.tickets_included_enabled ? "bg-pink-600" : "bg-slate-700"}`}
                            >
                              <span
                                className={`inline-block w-4 h-4 bg-white rounded-full shadow transform transition-transform mt-0.5 ${selectedTable.tickets_included_enabled ? "translate-x-5" : "translate-x-0.5"}`}
                              />
                            </button>
                          </div>
                          {selectedTable.tickets_included_enabled && (
                            <Field label="Select Ticket Type">
                              <select
                                value={
                                  selectedTable.tickets_included_ticket_type ||
                                  ""
                                }
                                onChange={(e) =>
                                  handleFormChange(
                                    "tickets_included_ticket_type",
                                    e.target.value,
                                  )
                                }
                                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                              >
                                <option value="">Select ticket type</option>
                                {ticketTypes.map((tk) => (
                                  <option key={tk.id} value={tk.id}>
                                    {tk.name}
                                  </option>
                                ))}
                              </select>
                            </Field>
                          )}
                        </div>

                        <div className="bg-slate-800/40 border border-white/5 rounded-lg p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="text-sm text-slate-300">
                              Merchandise Included
                            </label>
                            <button
                              onClick={() =>
                                handleFormChange(
                                  "merchandise_enabled",
                                  !selectedTable.merchandise_enabled,
                                )
                              }
                              className={`relative inline-flex w-10 h-5 rounded-full transition-colors ${selectedTable.merchandise_enabled ? "bg-pink-600" : "bg-slate-700"}`}
                            >
                              <span
                                className={`inline-block w-4 h-4 bg-white rounded-full shadow transform transition-transform mt-0.5 ${selectedTable.merchandise_enabled ? "translate-x-5" : "translate-x-0.5"}`}
                              />
                            </button>
                          </div>
                          {selectedTable.merchandise_enabled && (
                            <Field label="Merchandise Description">
                              <textarea
                                value={
                                  selectedTable.merchandise_description || ""
                                }
                                onChange={(e) =>
                                  handleFormChange(
                                    "merchandise_description",
                                    e.target.value,
                                  )
                                }
                                placeholder="Describe the merchandise included..."
                                rows={3}
                                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40 resize-none"
                              />
                            </Field>
                          )}
                        </div>

                        <Field label="Status">
                          <div className="grid grid-cols-3 gap-2">
                            {["available", "pending", "reserved"].map((s) => (
                              <button
                                key={s}
                                onClick={() =>
                                  handleFormChange("reservation_status", s)
                                }
                                className={`py-2 rounded-lg text-xs font-medium capitalize border transition ${selectedTable.reservation_status === s ? (s === "available" ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-400" : s === "pending" ? "bg-amber-500/20 border-amber-500/50 text-amber-400" : "bg-red-500/20 border-red-500/50 text-red-400") : "bg-slate-800 border-white/10 text-slate-400 hover:border-white/20 hover:text-white"}`}
                              >
                                {s}
                              </button>
                            ))}
                          </div>
                        </Field>

                        {selectedTable.reservation_status === "reserved" && (
                          <div className="bg-slate-800/40 border border-white/5 rounded-lg p-4 space-y-3">
                            <p className="text-xs text-slate-400 uppercase tracking-widest font-medium">
                              Reservation Info
                            </p>
                            <Field label="Customer Name">
                              <input
                                type="text"
                                value={selectedTable.reservation_name || ""}
                                onChange={(e) =>
                                  handleFormChange(
                                    "reservation_name",
                                    e.target.value,
                                  )
                                }
                                placeholder="Full name"
                                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                              />
                            </Field>
                            <div className="grid grid-cols-2 gap-4">
                              <Field label="Phone Number">
                                <input
                                  type="tel"
                                  value={selectedTable.reservation_phone || ""}
                                  onChange={(e) =>
                                    handleFormChange(
                                      "reservation_phone",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="+1 234 567 890"
                                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                                />
                              </Field>
                              <Field label="Email Address">
                                <input
                                  type="email"
                                  value={selectedTable.reservation_email || ""}
                                  onChange={(e) =>
                                    handleFormChange(
                                      "reservation_email",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="email@example.com"
                                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                                />
                              </Field>
                            </div>
                            <Field label="Full Delivery Address">
                              <textarea
                                value={selectedTable.reservation_address || ""}
                                onChange={(e) =>
                                  handleFormChange(
                                    "reservation_address",
                                    e.target.value,
                                  )
                                }
                                placeholder="Street, City, Postcode, Country"
                                rows={2}
                                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40 resize-none"
                              />
                            </Field>
                          </div>
                        )}

                        <Field label="Table Description">
                          <textarea
                            value={selectedTable.description || ""}
                            onChange={(e) =>
                              handleFormChange("description", e.target.value)
                            }
                            placeholder="e.g. Great view toward DJ booth and stage."
                            rows={3}
                            className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40 resize-none"
                          />
                        </Field>
                      </>
                    )}
                  </div>

                  {!isViewOnly && (
                    <div className="px-5 py-4 border-t border-white/10 flex justify-end flex-shrink-0">
                      {saveError && (
                        <div className="px-3 py-2.5 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-400">
                          {saveError}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center gap-3 text-slate-500 p-8 text-center">
                  <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center">
                    <PencilIcon className="w-5 h-5" />
                  </div>
                  <p className="text-sm">
                    {mapEditMode
                      ? "Exit edit mode to select a table"
                      : "Select a table on the map"}
                  </p>
                  <p className="text-xs text-slate-600">
                    {mapEditMode
                      ? "Click 'Done Editing' when finished"
                      : "Click any hotspot to view its details"}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* ── Tables list ── */}
          <div className="bg-slate-900/60 border border-white/10 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-white/10">
              <h3 className="text-xs uppercase tracking-widest text-slate-400 font-medium">
                All Tables
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table
                className="w-full border-collapse"
                style={{ minWidth: "600px" }}
              >
                <thead>
                  <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-400">
                    <th className="px-5 py-3 text-left">Name</th>
                    <th className="px-5 py-3 text-center">Price</th>
                    <th className="px-5 py-3 text-center">Drinks Credit</th>
                    <th className="px-5 py-3 text-center">Capacity</th>
                    <th className="px-5 py-3 text-center">Status</th>
                    {/* hide Actions column for viewers */}
                    {!isViewOnly && (
                      <th className="px-5 py-3 text-right">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {tables.map((t) => (
                    <tr
                      key={t.id}
                      onClick={() => {
                        if (!mapEditMode) selectTable(t.id);
                      }}
                      className={`border-b border-white/5 last:border-none hover:bg-slate-800/40 transition cursor-pointer
                        ${selectedId === t.id && !mapEditMode ? "bg-slate-800/60" : ""}`}
                    >
                      <td className="px-5 py-3 text-sm font-medium text-white">
                        {t.name}
                      </td>
                      <td className="px-5 py-3 text-sm text-slate-300 text-center">
                        {t.price ? (
                          `${getCurrencySymbol(event.currency)} ${t.price}`
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-sm text-slate-300 text-center">
                        {t.drinks_credit_amount ? (
                          `${getCurrencySymbol(event.currency)} ${t.drinks_credit_amount}`
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-sm text-slate-300 text-center">
                        {t.capacity || (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-center">
                        {t.reservation_status ? (
                          <span
                            className={`px-2 py-0.5 rounded-md text-xs font-medium capitalize border ${statusBadgeStyle(t.reservation_status)}`}
                          >
                            {t.reservation_status}
                          </span>
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                      {/* hide edit/delete for viewers */}
                      {!isViewOnly && (
                        <td
                          className="px-5 py-3 text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => handleEditOpen(t)}
                              className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-700 transition"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteTableId(t.id)}
                              className="p-1.5 rounded-md text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                  {!tables.length && (
                    <tr>
                      <td
                        colSpan={isViewOnly ? 5 : 6}
                        className="px-5 py-10 text-center text-slate-400 text-sm"
                      >
                        No tables detected yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* modals only for non-viewers */}
      {!isViewOnly && !!editingTable && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-sm bg-slate-900 border border-white/10 rounded-xl shadow-xl">
            <div className="px-6 py-4 border-b border-white/10">
              <h2 className="text-white font-semibold text-sm">
                Edit Table — {editingTable.name}
              </h2>
            </div>
            <div className="px-6 py-5 space-y-4">
              <Field label={`Price (${getCurrencySymbol(event.currency)})`}>
                <input
                  type="number"
                  value={editForm.price}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, price: e.target.value }))
                  }
                  placeholder="0.00"
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                />
              </Field>
              <Field
                label={`Drinks Credit (${getCurrencySymbol(event.currency)})`}
              >
                <input
                  type="number"
                  value={editForm.drinks_credit_amount}
                  onChange={(e) =>
                    setEditForm((p) => ({
                      ...p,
                      drinks_credit_amount: e.target.value,
                    }))
                  }
                  placeholder="0.00"
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                />
              </Field>

              <Field label="Capacity (Guests)">
                <input
                  type="number"
                  value={editForm.capacity}
                  onChange={(e) =>
                    setEditForm((p) => ({
                      ...p,
                      capacity: e.target.value,
                    }))
                  }
                  placeholder="0"
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                />
              </Field>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
              <button
                onClick={() => setEditingTable(null)}
                className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={editSaving}
                className="px-4 py-2 text-sm rounded-lg text-white bg-pink-600 hover:bg-pink-700 disabled:opacity-50 transition flex items-center gap-2"
              >
                {editSaving ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {!isViewOnly && !!deleteTableId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-sm bg-slate-900 border border-white/10 rounded-xl shadow-xl">
            <div className="px-6 py-5">
              <h2 className="text-white font-semibold text-sm mb-1">
                Delete Table
              </h2>
              <p className="text-slate-400 text-sm">
                Are you sure you want to remove this table? This action cannot
                be undone.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
              <button
                onClick={() => setDeleteTableId(null)}
                className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteTableId)}
                className="px-4 py-2 text-sm rounded-lg text-white bg-red-600 hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer>
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </ToastContainer>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="block text-xs text-slate-400">{label}</label>
    {children}
  </div>
);

export default TablesTab;
